/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { throttle } from '../../services';
import { getBreakpoint } from '../../services/breakpoint';
export var EuiShowFor = ({
  children,
  sizes
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getBreakpoint(typeof window === 'undefined' ? -Infinity : window.innerWidth));
  const functionToCallOnWindowResize = throttle(() => {
    const newBreakpoint = getBreakpoint(window.innerWidth);

    if (newBreakpoint !== currentBreakpoint) {
      setCurrentBreakpoint(newBreakpoint);
    } // reacts every 50ms to resize changes and always gets the final update

  }, 50); // Add window resize handlers

  useEffect(() => {
    window.addEventListener('resize', functionToCallOnWindowResize);
    return () => {
      window.removeEventListener('resize', functionToCallOnWindowResize);
    };
  }, [sizes, functionToCallOnWindowResize]);

  if (sizes === 'all' || sizes.includes(currentBreakpoint)) {
    return <>{children}</>;
  }

  return null;
};
EuiShowFor.propTypes = {
  /**
     * Required otherwise nothing ever gets returned
     */
  children: PropTypes.node.isRequired,

  /**
     * List of all the responsive sizes to show the children for.
     * Array of #EuiBreakpointSize
     */
  sizes: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(["xs", "s", "m", "l", "xl"]).isRequired).isRequired, PropTypes.oneOf(["all", "none"])]).isRequired
};

try {
  EuiShowFor.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/responsive/show_for.tsx',
    description: '',
    displayName: 'EuiShowFor',
    methods: [],
    props: {
      children: {
        defaultValue: null,
        description: 'Required otherwise nothing ever gets returned',
        name: 'children',
        parent: {
          fileName: 'app/src/components/responsive/show_for.tsx',
          name: 'EuiShowForProps'
        },
        declarations: [{
          fileName: 'app/src/components/responsive/show_for.tsx',
          name: 'EuiShowForProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      sizes: {
        defaultValue: null,
        description: 'List of all the responsive sizes to show the children for.\n' + 'Array of #EuiBreakpointSize',
        name: 'sizes',
        parent: {
          fileName: 'app/src/components/responsive/show_for.tsx',
          name: 'EuiShowForProps'
        },
        declarations: [{
          fileName: 'app/src/components/responsive/show_for.tsx',
          name: 'EuiShowForProps'
        }],
        required: true,
        type: {
          name: '"none" | "all" | EuiBreakpointSize[]'
        }
      }
    },
    extendedInterfaces: ['EuiShowForProps']
  };
} catch (e) {}